import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import { formatViews } from '@/components/feature/MovieCard';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Detail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);
  const [toast, setToast] = useState('');

  usePageMeta({
    title: movie
      ? `${movie.title}剧情介绍及观看指南 - 韩国伦理`
      : '影视作品不存在 - 韩国伦理',
    description: movie
      ? `${movie.title}（${movie.year}）由${movie.director}执导，${movie.cast.join('、')}主演。${movie.summary}`
      : '抱歉，你访问的影视作品不存在或已下架。',
    keywords: movie
      ? `${movie.title},韩国伦理,${movie.categoryLabel},${movie.genre},韩国伦理影视剧情`
      : '韩国伦理,影视作品不存在',
  });

  const related = useMemo(() => {
    if (!movie) return [];
    return movies.filter((m) => m.id !== movie.id && m.category === movie.category).slice(0, 4);
  }, [movie]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleCopy = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  if (!movie) {
    return (
      <div className="pt-16 min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-3xl">
            <i className="ri-file-unknow-line" />
          </div>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">影视作品不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的作品可能已被移除，或链接有误。</p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Link
              to="/category"
              className="px-6 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              浏览影视分类
            </Link>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16">
      {/* Header band */}
      <div className="w-full px-4 md:px-8 py-6 bg-primary-950">
        <div className="max-w-7xl mx-auto">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-primary-300/80" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-accent-400 transition-colors">影视分类</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category?cat=${movie.category}`} className="hover:text-accent-400 transition-colors">
              {movie.categoryLabel}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">{movie.title}</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="max-w-7xl mx-auto">
          {/* Top: cover + info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="w-full max-w-[320px] mx-auto lg:mx-0 rounded-lg overflow-hidden border border-background-200 bg-background-200">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 高清封面`}
                  title={`${movie.title} 韩国伦理影视剧情`}
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              <div className="mt-5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-link" /> 复制链接
                </button>
                <button
                  type="button"
                  onClick={() => showToast('社交分享为前端演示效果')}
                  className="w-11 h-11 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors"
                  aria-label="分享到社交平台"
                >
                  <i className="ri-share-line text-lg" />
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                {['ri-weibo-fill', 'ri-wechat-fill', 'ri-twitter-x-fill'].map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => showToast('社交分享为前端演示效果')}
                    className="flex-1 h-10 flex items-center justify-center rounded-md bg-background-100 border border-background-200 text-foreground-600 hover:text-accent-600 hover:bg-secondary-100 transition-colors"
                    aria-label="分享"
                  >
                    <i className={`${icon} text-lg`} />
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900">
                  {movie.categoryLabel}
                </span>
                <span className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600">
                  {movie.genre}
                </span>
                <span className="px-3 py-1 rounded-full text-xs bg-accent-100 text-accent-900">{movie.status}</span>
              </div>
              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">{movie.title}</h1>
              <p className="mt-3 text-base text-foreground-700 leading-relaxed">{movie.summary}</p>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-background-100 border border-background-200">
                  <div className="text-xs text-foreground-500">评分</div>
                  <div className="mt-1 font-heading text-xl font-bold text-accent-600">{movie.rating.toFixed(1)}</div>
                </div>
                <div className="p-4 rounded-lg bg-background-100 border border-background-200">
                  <div className="text-xs text-foreground-500">首映年份</div>
                  <div className="mt-1 font-heading text-xl font-bold text-foreground-950">{movie.year}</div>
                </div>
                <div className="p-4 rounded-lg bg-background-100 border border-background-200">
                  <div className="text-xs text-foreground-500">热度</div>
                  <div className="mt-1 font-heading text-xl font-bold text-foreground-950">{formatViews(movie.views)}</div>
                </div>
                <div className="p-4 rounded-lg bg-background-100 border border-background-200">
                  <div className="text-xs text-foreground-500">集数/时长</div>
                  <div className="mt-1 font-heading text-xl font-bold text-foreground-950">
                    {movie.category === 'film' ? '电影' : '16集'}
                  </div>
                </div>
              </div>

              <dl className="mt-6 space-y-3 text-sm">
                <div className="flex flex-wrap gap-2">
                  <dt className="w-16 text-foreground-500">导演</dt>
                  <dd className="flex-1 text-foreground-800">{movie.director}</dd>
                </div>
                <div className="flex flex-wrap gap-2">
                  <dt className="w-16 text-foreground-500">主演</dt>
                  <dd className="flex-1 text-foreground-800">{movie.cast.join('、')}</dd>
                </div>
                <div className="flex flex-wrap gap-2">
                  <dt className="w-16 text-foreground-500">类型</dt>
                  <dd className="flex-1 text-foreground-800">
                    {movie.categoryLabel} · {movie.genre}
                  </dd>
                </div>
                <div className="flex flex-wrap gap-2">
                  <dt className="w-16 text-foreground-500">所属分类</dt>
                  <dd className="flex-1">
                    <Link
                      to={`/category?cat=${movie.category}`}
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      {movie.categoryLabel} →
                    </Link>
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Plot */}
          <div className="mt-12 md:mt-16">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1 h-6 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">剧情介绍</h2>
            </div>
            <div className="space-y-5 max-w-4xl">
              {movie.plot.map((para, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                  {para}
                </p>
              ))}
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-14 md:mt-20">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-1 h-6 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关作品推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {related.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-primary-950 text-background-50 text-sm shadow-lg animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}